import React, { useState } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { FormSelect } from 'components/form';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import {
  Dialog,
  Stack,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import { docuSignApi } from '../../api';
import { SendEnvelopeVariables, TemplateOptionsResponse, TemplateOptionsVariables } from '../../api/types';

export const DocuSignDialog = () => {
  const history = useHistory();
  const { showNotification } = useNotifications();
  const { data, isOpen, handleClose } = useModal(ModalsKeys.DocuSignDialog);

  const [selectedValue, setSelectedValue] = useState<string>('');

  const [onSendEnvelope, { loading: isEnvelopeSending }] = useMutation<void, SendEnvelopeVariables>(
    docuSignApi.sendEnvelopeV2
  );

  const { loading: isDocusignTemplateListLoading, data: templatesOptionsResponse } = useQuery<
    TemplateOptionsResponse,
    TemplateOptionsVariables
  >(docuSignApi.getTemplates, {
    variables: {
      VOId: data.vendorOpportunityId,
    },
  });

  const handleGoToTemplate = () => {
    history.push({ pathname: `/docu/sign/${data.link}` });
  };

  const handleSubmit = async () => {
    try {
      await onSendEnvelope({
        variables: { VOId: data.vendorOpportunityId, input: { templateId: selectedValue } },
      });

      handleClose();
      showNotification('Successfully sent template!');
    } catch (err) {
      logError(err);
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="lg" onClose={handleClose}>
      <DialogTitle>Send via DocuSign</DialogTitle>

      <DialogContent dividers>
        <RenderWithFallback
          isLoading={isDocusignTemplateListLoading}
          fallBack={
            <Stack gap="0.5rem" alignItems="center" justifyContent="center">
              <Typography>Loading templates</Typography>
              <CircularProgress />
            </Stack>
          }
        >
          <FormSelect
            value={selectedValue}
            label="Templates for your Account"
            options={templatesOptionsResponse?.options ?? []}
            disabled={!templatesOptionsResponse?.options?.length}
            onChange={event => setSelectedValue(event.target.value as string)}
          />

          <RenderWithCondition condition={!!data.link}>
            <Button onClick={handleGoToTemplate}>View Created Proposal</Button>
          </RenderWithCondition>
        </RenderWithFallback>
      </DialogContent>

      <DialogActions>
        <Button disabled={isEnvelopeSending} onClick={handleClose}>
          Cancel
        </Button>

        <Button variant="contained" disabled={!selectedValue} loading={isEnvelopeSending} onClick={handleSubmit}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};
